import React from "react";
import {
  Button,
  Checkbox,
  Grid,
  Paper,
  Popper,
  TextField,
  Typography,
} from "@material-ui/core";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import { CheckBoxOutlineBlank, CheckBox, ExpandMore } from "@material-ui/icons";
import PropTypes from "prop-types";

import { ListItem } from "../ListItem/ListItem";

import { useStyles } from "./MultiSelectBox.styles";

/*
  Outside Render
  const options = [
    { label: "Country 1", value: "Country 1" },
    { label: "Country 2", value: "Country 2" },
    { label: "Country 3", value: "Country 3" },
    { label: "Country 4", value: "Country 4" },
    { label: "Country 5", value: "Country 5" },
    { label: "Country 6", value: "Country 6" },
    { label: "Country 7", value: "Country 7" },
    { label: "Country 8", value: "Country 8" },
    { label: "Country 9", value: "Country 9" },
    { label: "Country 10", value: "Country 10" },
  ];
  const [selectedOptions, setSelectedOptions] = useState([]);
  const getOptionLabel = (option) => `${option.label}`;
  const getOptionDisabled = (option) => option.value === "foo";
  const handleToggleOption = (selOptions) => setSelectedOptions(selOptions);
  const handleClearOptions = () => setSelectedOptions([]);
  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedOptions(options);
    } else {
      handleClearOptions();
    }
  };

  How to use the component
  <MultiSelectBox
    btnLabel="Add"
    btnIcon={<Add></Add>}
    onAdd={(a) => {
      console.log(a);
    }}
    emptyImg={releasetarget}
    description={
      <>
        <b>There are no targets selected yet.</b> &nbsp;Release is not
        being deistributed.
      </>
    }
    list={[]}
    items={options}
    getOptionLabel={getOptionLabel}
    getOptionDisabled={getOptionDisabled}
    selectedValues={selectedOptions}
    placeholder="Select countries/regions"
    selectAllLabel="Select All"
    onToggleOption={handleToggleOption}
    onClearOptions={handleClearOptions}
    onSelectAll={handleSelectAll}
    >
  </MultiSelectBox> 
*/

const MultiSelectBox = ({
  items,
  selectedValues,
  placeholder,
  selectAllLabel,
  noOptionsText,
  onToggleOption,
  onClearOptions,
  onSelectAll,
  getOptionLabel,
  getOptionType,
  getSelected,
  btnLabel,
  btnIcon,
  onAdd,
  onDelete,
  emptyImg,
  description,
  list,
  isAddEnabled,
  isDeleteEnabled,
  isListEnabled,
}) => {
  const classes = useStyles();
  const allSelected = items.length === selectedValues.length;

  const handleToggleSelectAll = () => {
    onSelectAll && onSelectAll(!allSelected);
  };

  const handleChange = (event, selectedOptions, reason) => {
    if (reason === "select-option" || reason === "remove-option") {
      if (selectedOptions.find((option) => option.code === "select-all")) {
        handleToggleSelectAll();
      } else {
        onToggleOption && onToggleOption(selectedOptions);
      }
    } else if (reason === "clear") {
      onClearOptions && onClearOptions();
    }
  };

  const add = () => {
    if (selectedValues) {
      onAdd(selectedValues);
    }
  };

  const optionRenderer = (option, { selected }) => {
    const selectAllProps =
      option.code === "select-all" // To control the state of 'select-all' checkbox
        ? { checked: allSelected }
        : {};
    return (
      <>
        <div className={classes.alignItem}>
          <div className={classes.alignLabel}>
            <Checkbox
              name={option.code}
              color="primary"
              icon={<CheckBoxOutlineBlank />}
              checkedIcon={<CheckBox />}
              checked={selected}
              {...selectAllProps}
            />
            {getOptionLabel(option)}
          </div>
          <Typography variant="h6" className={classes.type}>
            {option?.type ? getOptionType(option) : ""}
          </Typography>
        </div>
      </>
    );
  };

  const inputRenderer = (params) => (
    <TextField {...params} placeholder={placeholder} />
  );

  const getOptionSelected = (option, anotherOption) =>
    option.code === anotherOption.code;

  const filter = createFilterOptions();

  const PopperMulti = function (props) {
    return (
      <Popper
        {...props}
        style={{
          width: props.style.width + 24,
          padding: "16px 0",
        }}
        placement="bottom"
      />
    );
  };

  const PaperMulti = function (props) {
    return <Paper {...props} className={classes.paper} />;
  };

  return (
    <div id="multiSelectBox" className={classes.multiInputBox}>
      {isAddEnabled && (
        <div className={classes.inputBox}>
          <Autocomplete
            // open={true}
            multiple
            fullWidth
            className={classes.input}
            limitTags={3}
            options={items}
            value={selectedValues}
            disableCloseOnSelect
            getOptionLabel={getOptionLabel}
            getOptionSelected={getSelected ? getSelected : getOptionSelected}
            noOptionsText={noOptionsText}
            PopperComponent={PopperMulti}
            PaperComponent={PaperMulti}
            popupIcon={<ExpandMore></ExpandMore>}
            filterOptions={(options, params) => {
              const filtered = filter(options, params);
              return [
                { code: "select-all", name: selectAllLabel },
                ...filtered,
              ];
            }}
            onChange={handleChange}
            renderOption={optionRenderer}
            renderInput={inputRenderer}
          />
          <Button
            color="primary"
            disableRipple
            className={classes.add}
            onClick={add}
          >
            {btnIcon}
            {btnLabel}
          </Button>
        </div>
      )}
      {isListEnabled && (
        <>
          {list.length === 0 ? (
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              className={classes.empty}
            >
              <img src={emptyImg} className={classes.img} alt="empty"></img>
              <Typography variant="h6" className={classes.desc}>
                {description}
              </Typography>
            </Grid>
          ) : (
            <Grid className={classes.list}>
              {list.map((item, index) => {
                return (
                  <ListItem
                    key={index}
                    label={item.name}
                    isDeleteEnabled={isDeleteEnabled}
                    del={() => {
                      onDelete(item);
                    }}
                  ></ListItem>
                );
              })}
            </Grid>
          )}
        </>
      )}
    </div>
  );
};

MultiSelectBox.defaultProps = {
  items: [],
  selectedValues: [],
  getOptionLabel: (value) => value,
  isAddEnabled: true,
  isDeleteEnabled: true,
  isListEnabled: true,
};

MultiSelectBox.propTypes = {
  items: PropTypes.array,
  selectedValues: PropTypes.array,
  placeholder: PropTypes.string,
  selectAllLabel: PropTypes.string,
  noOptionsText: PropTypes.any,
  onToggleOption: PropTypes.func,
  onClearOptions: PropTypes.func,
  onSelectAll: PropTypes.func,
  getOptionLabel: PropTypes.func,
  getOptionType: PropTypes.func,
  getSelected: PropTypes.func,
  list: PropTypes.array,
  btnLabel: PropTypes.string,
  btnIcon: PropTypes.node,
  onAdd: PropTypes.func,
  onDelete: PropTypes.func,
  emptyImg: PropTypes.any,
  description: PropTypes.node,
  style: PropTypes.any,
  isAddEnabled: PropTypes.bool,
  isDeleteEnabled: PropTypes.bool,
  isListEnabled: PropTypes.bool,
};

export { MultiSelectBox };
